import { effect, inject, Injectable, signal } from '@angular/core';
import { APIService } from './api';
import { NgxSpinnerService } from 'ngx-spinner';
import { LocaleService } from './locale';
import { CompanyService } from './company';
import { PaymentService } from './payment';

@Injectable({
  providedIn: 'root',
})
export class CalculationService {
  private api = inject(APIService);
  private spinner = inject(NgxSpinnerService);
  private localeService = inject(LocaleService);
  private companyService = inject(CompanyService);
  private paymentService = inject(PaymentService);

  locale = this.localeService.locale();
  company = this.companyService.company();

  calculations = signal<any>([]);

  constructor() {
    effect(() => {
      this.locale = this.localeService.locale();
      this.company = this.companyService.company();
      this.getCalculations();
    });
  }

  getStatus = (calculation: any) => {
    if (!calculation.answers) return calculation;

    // Defensive guard: avoid blocking the UI when legacy answers include large inline blobs.
    if (
      typeof calculation.answers === 'string' &&
      calculation.answers.length > 1_000_000
    ) {
      calculation.percent = '0 %';
      calculation.scope = this.locale.category.heading_category + ' 1';
      return calculation;
    }

    try {
      const parsed = JSON.parse(calculation.answers);
      if (!Array.isArray(parsed)) return calculation;
      const allQuestions = parsed.flatMap((c: any) => c.questions || []);
      const doneQuestions = allQuestions.filter((q: any) => q.done === true);
      const completedCategories = parsed.filter(
        (category: any) =>
          Array.isArray(category.questions) &&
          category.questions.length > 0 &&
          category.questions.every((q: any) => q.done === true),
      );
      const totalQuestions = allQuestions.length;
      const completedQuestionCount = doneQuestions.length;
      const completedCategoryCount = completedCategories.length;
      calculation.percent =
        totalQuestions > 0
          ? Math.round((completedQuestionCount / totalQuestions) * 100) + ' %'
          : '0 %';
      if (completedCategoryCount === parsed.length && parsed.length > 0) {
        calculation.scope = 'Summary';
      } else if (completedCategoryCount > 0) {
        calculation.scope =
          this.locale.category.heading_category +
          ' ' +
          (completedCategoryCount + 1);
      } else {
        calculation.scope = this.locale.category.heading_category + ' 1';
      }
    } catch (e) {
      console.error('Status parse error', e);
    }
    return calculation;
  };

  sortCalculations = (a: any, b: any) => {
    if (a.createdAt < b.createdAt) {
      return -1;
    }
    if (a.createdAt > b.createdAt) {
      return 1;
    }
    return 0;
  };

  getCalculations = async () => {
    this.spinner.show();

    if (!this.company?.id) {
      this.spinner.hide();
      return;
    }

    const companyDetails = await this.api.CompanyDetailsByCompanyId(
      this.company.id,
    );

    let calculations: any = [];

    if (companyDetails?.items?.length > 0) {
      const sorted = companyDetails.items.sort(this.sortCalculations);

      sorted.map((calc: any) => {
        calculations.push(
          this.getStatus({
            id: calc.id,
            name: calc.calculationName,
            date: calc.startDate,
            scope: this.locale.category.heading_category + ' 1',
            percent: '0 %',
            companyId: this.company.id,
            answers: calc.answers,
            paidForLogo: calc.paidForLogo,
            paidForReport: calc.paidForReport,
          }),
        );
      });
      this.calculations.set(calculations?.reverse());
    } else {
      this.calculations.set([]);
    }

    this.spinner.hide();
  };

  createCalculation = async (form: any) => {
    this.spinner.show();

    const details = this.paymentService.getCurrentPlanDetails();

    const calculation = await this.api.CreateCompanyDetails({
      companyId: this.company.id,
      calculationName: form.controls.name.value,
      startDate: form.controls.date.value,
      paidForReport: details?.reportsIncluded,
      paidForLogo: details?.logoIncluded,
    });

    this.getCalculations();

    return calculation;
  };
}
